import {
  useCreateVendorBilling,
  useGetVendorBilling,
} from "@entities/campaigns/lib/hooks";
import styles from "./styles.module.scss";
import { Avatar, Button, Spin, Alert, Modal, Flex } from "antd";
import { CheckCircleFilled, EditOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import {
  BillingEntity,
  ICreateVendorBilling,
} from "@shared/lib/types/briefCampains";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { AddBillingDetails } from "./AddBillingDeatils";
import { useAttachVendorBilling } from "@entities/campaigns/lib/hooks/useAttachVendorBilling";
import { PanVerificationBadge } from "./ui/PanVerificationBadge";
import {
  getInitialPanStatus,
  mapBillingEntityToFormValues,
} from "./lib/mapBillingEntityToFormValues";

interface SelectVendorBillingProps {
  influencerId: string;
  name: string;
  profilePicUrl: string;
  onClose: () => void;
  campaignInfluencerId: string;
  isEdit?: boolean;
}

type AddBillingModalState = {
  type: "Influencer" | "Agency";
  agencyId?: string;
  contact?: string;
  visible: boolean;
  billingEntity?: BillingEntity | null;
};

export function SelectVendorBilling({
  influencerId,
  name,
  profilePicUrl,
  onClose,
  campaignInfluencerId,
  isEdit = false,
}: SelectVendorBillingProps) {
  const { briefId } = useBriefStore();
  const { vendorBilling, isLoading, isError } =
    useGetVendorBilling(influencerId);
  const [selected, setSelected] = useState<string | null>(null);

  const [addBillingProps, setAddBillingProps] = useState<AddBillingModalState>({
    type: "Influencer",
    agencyId: undefined,
    contact: undefined,
    visible: false,
    billingEntity: null,
  });

  const { createVendorBilling, isCreated, isCreating } = useCreateVendorBilling(
    briefId,
    influencerId
  );

  const { attachVendorBilling, isAttaching, isAttached } =
    useAttachVendorBilling(briefId, influencerId);

  const openAddBilling = (props: Omit<AddBillingModalState, "visible">) => {
    setAddBillingProps({
      ...props,
      visible: true,
      billingEntity: props.billingEntity ?? null,
    });
  };

  const closeAddBilling = () => {
    setAddBillingProps((prev) => ({
      ...prev,
      visible: false,
      billingEntity: null,
    }));
  };

  const renderBillingDetails = (
    handleEdit: () => void,
    billingEntity?: BillingEntity
  ) => {
    if (!billingEntity) return null;
    return (
      <Flex
        justify="space-between"
        align="center"
        style={{ width: "100%" }}
        gap={12}
      >
        <Flex className={styles.billingContact} vertical>
          <div>
            {billingEntity.phoneNumber &&
              `${billingEntity?.countryCode} ${billingEntity?.phoneNumber}`}
          </div>
          <div>{billingEntity?.emailId || ""}</div>
        </Flex>
        <Flex vertical gap={6} align="flex-start">
          <span className={styles.billingPan}>
            {billingEntity.panNumber}
            {billingEntity.tdsPercentage
              ? ` (TDS ${billingEntity.tdsPercentage}%)`
              : ""}
          </span>
          <PanVerificationBadge billingEntity={billingEntity} />
        </Flex>
        <span className={styles.billingGst}>
          {billingEntity.gstNumber
            ? `${billingEntity.gstNumber} (GST ${billingEntity.gstPercentage}%)`
            : ""}
        </span>
        <Button
          size="small"
          type="default"
          icon={<EditOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        />
      </Flex>
    );
  };

  const handleAddBilling = (values: ICreateVendorBilling) => {
    createVendorBilling({
      vendorBilling: values,
      type: addBillingProps.type,
      agencyId: addBillingProps.agencyId,
      contact: addBillingProps.contact,
    });
  };

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Billing added successfully");
      closeAddBilling();
    }
  }, [isCreated]);

  useEffect(() => {
    if (isAttached) {
      ToastService.success(
        isEdit
          ? "Billing updated successfully"
          : "Billing attached successfully"
      );
      onClose();
    }
  }, [isAttached, isEdit]);

  if (isLoading)
    return (
      <div className={styles.center}>
        <Spin />
      </div>
    );
  if (isError)
    return <Alert type="error" message="Failed to load billing info" />;

  const isEditingBilling = Boolean(addBillingProps.billingEntity);
  const formInitialValues = addBillingProps.billingEntity
    ? mapBillingEntityToFormValues(addBillingProps.billingEntity)
    : null;
  const formInitialPanStatus = getInitialPanStatus(
    addBillingProps.billingEntity
  );

  return (
    <div>
      <div className={styles.header}>
        <Avatar src={profilePicUrl} size={64} />
        <div className={styles.name}>{name}</div>
      </div>
      <div className={styles.infoBox}>
        <Alert
          type="info"
          message="The payout link will be sent to the contact you choose."
          showIcon
        />
      </div>
      <div className={styles.billingSection}>
        <div className={styles.billingTitle}>Billing</div>
        {vendorBilling?.self.map((item) => (
          <div
            key={item.billingEntity?._id}
            className={`${styles.billingRow} ${
              selected && selected === item.billingEntity?._id
                ? styles.selected
                : ""
            }`}
            onClick={() => {
              if (item.billingEntity?._id) setSelected(item.billingEntity?._id);
            }}
          >
            <span className={styles.radio}>
              {selected === item.billingEntity?._id ? (
                <CheckCircleFilled />
              ) : (
                <span className={styles.circle} />
              )}
            </span>
            <span className={styles.labelSelf}>Self</span>
            <Flex vertical>
              {" "}
              <span className={styles.billingName}>{name}</span>
              <span className={styles.billingType}>Self</span>
            </Flex>
            {item.isBillingFound ? (
              renderBillingDetails(
                () =>
                  openAddBilling({
                    type: "Influencer",
                    contact: item.influencer?._id,
                    billingEntity: item.billingEntity,
                  }),
                item.billingEntity
              )
            ) : (
              <Flex justify="space-between" style={{ width: "100%" }}>
                <span className={styles.billingInfo}>
                  Information Not added
                </span>
                <Button
                  size="small"
                  type="default"
                  onClick={() =>
                    openAddBilling({
                      type: "Influencer",
                      contact: item.influencer?._id,
                      billingEntity: null,
                    })
                  }
                >
                  Add Billing for Influencer
                </Button>
              </Flex>
            )}
          </div>
        ))}
        {vendorBilling?.linkedAgencies.map((item) => (
          <div
            key={item?.billingEntity?._id}
            className={`${styles.billingRow} ${
              selected && selected === item?.billingEntity?._id
                ? styles.selected
                : ""
            }`}
            onClick={() => {
              if (item.billingEntity?._id) setSelected(item.billingEntity?._id);
            }}
          >
            <span className={styles.radio}>
              {selected === item?.billingEntity?._id ? (
                <CheckCircleFilled />
              ) : (
                <span className={styles.circle} />
              )}
            </span>

            <span className={styles.labelAgency}>{item.agency.name}</span>
            <Flex vertical>
              <span className={styles.billingName}>{name}</span>
              <span className={styles.billingType}>
                {item.billingEntity?.billingType || "—"}
              </span>
            </Flex>
            {item.isBillingFound && item.billingEntity ? (
              renderBillingDetails(
                () =>
                  openAddBilling({
                    type: "Agency",
                    agencyId: item.agency._id,
                    billingEntity: item.billingEntity,
                  }),
                item.billingEntity
              )
            ) : (
              <Flex justify="space-between" style={{ width: "100%" }}>
                <span className={styles.billingInfo}>
                  Information Not added
                </span>
                <Button
                  size="small"
                  type="default"
                  onClick={() =>
                    openAddBilling({
                      type: "Agency",
                      agencyId: item.agency._id,
                      billingEntity: null,
                    })
                  }
                >
                  Add Billing for Agency
                </Button>
              </Flex>
            )}
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <Button
          type="primary"
          disabled={!selected}
          loading={isAttaching}
          onClick={() =>
            attachVendorBilling({
              vendorBillingId: selected || "",
              campaignInfluencerId: campaignInfluencerId,
              isEdit: isEdit,
            })
          }
        >
          {isEdit ? "Update billing" : "Add billing"}
        </Button>
      </div>
      <Modal
        open={addBillingProps.visible}
        destroyOnClose
        footer={null}
        title={`${isEditingBilling ? "Edit" : "Add"} Billing for ${addBillingProps.type}`}
        onCancel={closeAddBilling}
      >
        <AddBillingDetails
          type={addBillingProps.type}
          onAdd={handleAddBilling}
          loading={isCreating}
          initialValues={formInitialValues}
          initialPanStatus={formInitialPanStatus}
        />
      </Modal>
    </div>
  );
}
